import Select, { FormatOptionLabelMeta } from '@atlaskit/select';
import {
  BrokenDatabase,
  Database,
  DatabaseField,
  EntryCreateOptions,
  EntryWithContext,
} from '@databases/types/database';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n';
import { isCreate } from '../entryPicker/DatabaseEntryPicker';

import DatabaseValuePickerOption from './DatabaseValuePickerOption';

type Option = {
  label: string;
  value: string;
  data: DatabaseField;
};

export const getOptionValue = (value: DatabaseField) => value.id;

export interface DatabaseValuePickerProps {
  selectedDatabase: Database | BrokenDatabase | undefined;
  selectedEntry: EntryWithContext | EntryCreateOptions | undefined;
  selectedField: DatabaseField | undefined;
  setSelectedField: (value: DatabaseField | undefined) => void;
}

const DatabaseValuePicker: FunctionComponent<DatabaseValuePickerProps> = ({
  selectedDatabase,
  selectedEntry,
  selectedField,
  setSelectedField,
}) => {
  const { formatMessage } = useIntl();

  const isDisabled = useMemo(
    () =>
      !selectedDatabase ||
      isBrokenDatabase(selectedDatabase) ||
      !selectedEntry ||
      isCreate(selectedEntry),
    [selectedDatabase, selectedEntry]
  );

  const formatOptionLabel = useCallback(
    (
      value: DatabaseField,
      meta: FormatOptionLabelMeta<DatabaseField>
    ): React.ReactNode => {
      if (meta.context === 'menu') {
        return <DatabaseValuePickerOption label={value.name} />;
      } else {
        return value.name;
      }
    },
    []
  );

  const resolvedOptions = useMemo(() => {
    if (
      !selectedDatabase ||
      isBrokenDatabase(selectedDatabase) ||
      !selectedEntry ||
      isCreate(selectedEntry)
    ) {
      return [];
    }

    return selectedDatabase.fields.map((fieldId) => selectedDatabase.fieldsById[fieldId]);
  }, [selectedDatabase, selectedEntry]);

  const handleChange = useCallback(
    (value: DatabaseField | null) => {
      setSelectedField(value ?? undefined);
    },
    [setSelectedField]
  );

  const filterOption = useCallback((option: Option, inputValue: string) => {
    return option.data.name.toLowerCase().includes(inputValue.toLowerCase());
  }, []);

  return (
    <Select<DatabaseField>
      inputId="reference-value-picker-input"
      options={resolvedOptions}
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      value={selectedField ?? null}
      getOptionValue={getOptionValue}
      onChange={handleChange}
      placeholder={formatMessage(messages['reference-value-picker.placeholder'])}
      noOptionsMessage={() =>
        formatMessage(messages['reference-value-picker.no-options'])
      }
      isDisabled={isDisabled}
      isClearable={true}
    />
  );
};

export default DatabaseValuePicker;
